import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import "@/types/router"; // Importar as extensões de tipos

import Login from "@/views/Login.vue";
import Dashboard from "@/views/Dashboard.vue";
import NotFound from "@/views/NotFound.vue";

const routes: Array<RouteRecordRaw> = [
	// ========== Auth Routes ==========
	{
		path: "/",
		// /testdesign: mantido por retrocompatibilidade dos links já compartilhados
		alias: "/testdesign",
		name: "Home",
		component: () => import("@/views/auth/Landing.vue"),
		meta: { requiresGuest: true, layout: "empty" },
	},
	{
		path: "/get-started",
		name: "GetStarted",
		component: () => import("@/views/auth/LandingPage.vue"),
		meta: { requiresGuest: true, layout: "empty" },
	},
	{
		path: "/termos",
		name: "Terms",
		component: () => import("@/views/legal/LegalPage.vue"),
		meta: { layout: "empty" },
		props: { type: "terms" },
	},
	{
		path: "/privacidade",
		name: "Privacy",
		component: () => import("@/views/legal/LegalPage.vue"),
		meta: { layout: "empty" },
		props: { type: "privacy" },
	},
	{
		path: "/pricing",
		name: "Pricing",
		component: () => import("@/views/auth/PricingPage.vue"),
		meta: { layout: "empty" },
	},
	{
		path: "/register-temple",
		name: "RegisterTemple",
		component: () => import("@/views/auth/RegisterTemple.vue"),
		meta: { requiresGuest: true, layout: "empty" },
	},
	{
		path: "/register-invite",
		// /convite: caminho amigável; /register-invite mantido por retrocompatibilidade
		alias: "/convite",
		name: "RegisterWithInvite",
		component: () => import("@/views/auth/RegisterWithInvite.vue"),
		meta: { layout: "empty" },
	},
	{
		path: "/verify-email",
		name: "VerifyEmail",
		component: () => import("@/views/auth/VerifyEmail.vue"),
		meta: { layout: "empty" },
	},
	{
		path: "/onboarding",
		name: "Onboarding",
		component: () => import("@/views/auth/Onboarding.vue"),
		meta: { requiresAuth: true, layout: "empty" },
	},
	{
		path: "/forgot-password",
		name: "ForgotPassword",
		component: () => import("@/views/auth/ForgotPassword.vue"),
		meta: { requiresGuest: true, layout: "empty" },
	},
	{
		path: "/reset-password",
		name: "ResetPassword",
		component: () => import("@/views/auth/ResetPassword.vue"),
		meta: { layout: "empty" },
	},
	{
		path: "/subscription-blocked",
		name: "SubscriptionBlocked",
		component: () => import("@/views/auth/SubscriptionBlocked.vue"),
		meta: { requiresAuth: true, layout: "empty" },
	},
	{
		path: "/pagar/:token",
		name: "PayPage",
		component: () => import("@/views/PayPage.vue"),
		meta: { layout: "empty" },
	},
	{
		path: "/agenda/:token",
		name: "PublicAgenda",
		component: () => import("@/views/PublicAgendaPage.vue"),
		meta: { layout: "empty" },
	},
	{
		path: "/agenda/:token/reserva/:confirmToken",
		name: "PublicReservation",
		component: () => import("@/views/PublicReservationPage.vue"),
		meta: { layout: "empty" },
	},
	// ========== App Routes ==========
	{
		// Destinatário responde ao pedido de transferência de propriedade. Aberta a
		// qualquer papel: quem recebe pode ser membro (link do e-mail).
		path: "/ownership-transfer",
		name: "OwnershipTransfer",
		component: () => import("@/views/ownership-transfer/OwnershipTransferPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/profile",
		name: "Profile",
		component: () => import("@/views/profile/ProfilePage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/people/:id",
		name: "PersonDetail",
		component: () => import("@/views/people/PersonDetailPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/consultants",
		name: "Consultants",
		component: () => import("@/views/people/ConsultantsPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/mediums",
		name: "Mediums",
		component: () => import("@/views/people/MediumsPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/journey-stages",
		name: "JourneyStages",
		component: () => import("@/views/journey-stages/JourneyStagePage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/functions",
		name: "Functions",
		component: () => import("@/views/functions/FunctionsPage.vue"),
		meta: { requiresAuth: true },
	},
	// Legacy redirect
	{
		path: "/users",
		redirect: "/temple/users",
	},
	{
		path: "/spiritual-lines",
		name: "SpiritualLines",
		component: () => import("@/views/spiritual-lines/SpiritualLinesPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/orixas",
		name: "Orixas",
		component: () => import("@/views/orixas/OrixasPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/guides",
		name: "Guides",
		component: () => import("@/views/guides/GuidesPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/guides/:id",
		name: "GuideDetail",
		component: () => import("@/views/guides/GuideDetailPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/ritual-types",
		name: "RitualTypes",
		component: () => import("@/views/ritual-types/RitualTypesPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/ritual-types/:id",
		name: "RitualTypeDetail",
		component: () => import("@/views/ritual-types/RitualTypeDetailPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/attendance-types",
		name: "AttendanceTypes",
		component: () => import("@/views/attendance-types/AttendanceTypesPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/attendance-types/:id",
		name: "AttendanceTypeDetail",
		component: () => import("@/views/attendance-types/AttendanceTypeDetailPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/rituals",
		name: "Rituals",
		component: () => import("@/views/rituals/RitualsPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/rituals/report",
		name: "RitualsGeneralReport",
		component: () => import("@/views/rituals/RitualsGeneralReportPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/rituals/:id",
		name: "RitualDetail",
		component: () => import("@/views/rituals/RitualDetailPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/ritual/:id/atendimento",
		name: "RitualAttendance",
		component: () => import("@/views/ritual-attendance/RitualAttendancePage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/ritual/:id/chamada",
		name: "RitualTicketCall",
		component: () => import("@/views/ticket-call/TicketCallView.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/ritual/:id/fechamento",
		name: "RitualClosing",
		component: () => import("@/views/rituals/RitualClosingPage.vue"),
		meta: { requiresAuth: true },
	},
	// Legacy redirect
	{
		path: "/ticket-call",
		redirect: "/rituals",
	},
	// ========== Device Auth Route (public, redirect to login if needed) ==========
	{
		path: "/device/authorize",
		name: "DeviceAuthorize",
		component: () => import("@/views/device/DeviceAuthorizePage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/autorizar",
		redirect: (to) => ({ path: "/device/authorize", query: to.query }),
	},
	// Reports de ritual removido — fechamento já existe dentro do próprio ritual
	// ========== Support Routes ==========
	{
		path: "/support",
		name: "Support",
		component: () => import("@/views/support/SupportTicketsPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/support/:id",
		name: "SupportTicketDetail",
		component: () => import("@/views/support/SupportTicketDetailPage.vue"),
		meta: { requiresAuth: true },
	},
	// ========== Temple Management Routes ==========
	{
		path: "/temple/settings",
		name: "TempleSettings",
		component: () => import("@/views/temple/TempleSettings.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/temple/invites",
		name: "TempleInvites",
		component: () => import("@/views/temple/InviteManagement.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/temple/users",
		name: "TempleUsers",
		component: () => import("@/views/users/UsersPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/temple/display-messages",
		name: "DisplayMessages",
		component: () => import("@/views/temple/DisplayMessagesPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/temple/wisdom-quotes",
		name: "WisdomQuotes",
		component: () => import("@/views/wisdom-quotes/WisdomQuotesPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/temple/import",
		name: "TempleImport",
		component: () => import("@/views/temple/ImportPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/temple/plan",
		name: "BillingPage",
		component: () => import("@/views/temple/BillingPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		// Retorno do checkout do Mercado Pago (back_url): agradece e faz polling
		// da ativação (que depende do webhook). Compartilhado por todos os fluxos.
		path: "/billing/retorno",
		name: "BillingReturn",
		component: () => import("@/views/billing/BillingReturn.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"], layout: "empty" },
	},
	// Legacy redirect
	{
		path: "/temple/members",
		redirect: "/temple/users",
	},
	// Download APK redirect
	{
		path: "/dl",
		name: "DownloadApk",
		component: () => import("@/views/DownloadRedirect.vue"),
	},
	{
		path: "/login",
		name: "Login",
		component: Login,
		meta: { requiresGuest: true, layout: "auth" },
	},
	// Legacy /register redireciona para landing
	{
		path: "/register",
		redirect: "/",
	},
	{
		path: "/logout",
		name: "Logout",
		component: () => import("@/views/Logout.vue"),
		meta: { requiresAuth: false },
	},
	{
		path: "/dashboard",
		name: "Dashboard",
		component: Dashboard,
		meta: { requiresAuth: true },
	},

	// ========== Member Groups Routes ==========
	{
		path: "/member-groups",
		name: "MemberGroups",
		component: () => import("@/views/member-group/MemberGroupListPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/member-groups/:id",
		name: "MemberGroupDetail",
		component: () => import("@/views/member-group/MemberGroupDetailPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},

	// ========== Financial Routes ==========
	{
		path: "/financial",
		name: "FinancialDashboard",
		component: () => import("@/views/financial/FinancialDashboardPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/financial/funds",
		name: "FinancialFunds",
		component: () => import("@/views/financial/FundsPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/financial/funds/:id",
		name: "FundDetail",
		component: () => import("@/views/financial/FundDetailPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/financial/movements",
		name: "FinancialMovements",
		component: () => import("@/views/financial/MovementsPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/financial/reports",
		name: "FinancialReports",
		component: () => import("@/views/financial/ReportsPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/financial/reports/:month",
		name: "FinancialReportDetail",
		component: () => import("@/views/financial/ReportDetailPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/financial/integrations",
		name: "FinancialIntegrations",
		component: () => import("@/views/financial/IntegrationsPage.vue"),
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},
	{
		path: "/financial/settings",
		name: "FinancialSettings",
		redirect: "/temple/settings",
		meta: { requiresAuth: true, roles: ["OWNER", "ADMIN"] },
	},

	// ========== Dev Routes ==========
	{
		path: "/dev",
		name: "DevIndex",
		component: () => import("@/views/dev/DevIndexPage.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/dev/icons",
		name: "IconShowcase",
		component: () => import("@/views/dev/IconShowcase.vue"),
		meta: { requiresAuth: true },
	},
	{
		path: "/dev/calendar",
		name: "DevCalendar",
		component: () => import("@/views/dev/CalendarPage.vue"),
		meta: { requiresAuth: true },
	},

	// general routes
	{
		path: "/:pathMatch(.*)*",
		name: "NotFound",
		component: NotFound,
	},
];

const router = createRouter({
	history: createWebHistory(),
	routes,
});

// Detectar falhas de chunk loading após deploy (hashes mudaram)
router.onError((error, to) => {
	const chunkFailedMessage = /Loading chunk|Failed to fetch dynamically imported module|import/i;
	if (chunkFailedMessage.test(error.message)) {
		// Evitar loop infinito: só recarrega 1x
		const lastReload = sessionStorage.getItem("chunk-reload");
		const now = Date.now().toString();
		if (!lastReload || Date.now() - parseInt(lastReload) > 10000) {
			sessionStorage.setItem("chunk-reload", now);
			window.location.href = to.fullPath;
		}
	}
});

// Guarda de navegação para verificar autenticação
router.beforeEach(async (to, from, next) => {
	const authStore = useAuthStore();
	const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
	const requiresGuest = to.matched.some((record) => record.meta.requiresGuest);
	const requiredPermission = to.meta.permission as string | undefined;
	const requiredRoles = to.meta.roles as string[] | undefined;

	// Verificar se o usuário está autenticado mas os dados do usuário não foram carregados
	if ((requiresAuth || requiresGuest) && authStore.isLoggedIn && !authStore.user) {
		try {
			await authStore.fetchUser();
		} catch (error) {
			console.error("Erro ao carregar dados do usuário:", error);
			// Se falhar ao carregar dados do usuário, tentar refresh token
			if (!(await authStore.refreshAccessToken())) {
				next({ name: "Login", query: { redirect: to.fullPath } });
				return;
			}
		}
	}

	if (requiresAuth && !authStore.isLoggedIn) {
		next({ name: "Login", query: { redirect: to.fullPath } });
	} else if (requiresGuest && authStore.isLoggedIn) {
		// Redirect to onboarding if needed, otherwise to dashboard
		if (authStore.needsOnboarding) {
			next({ name: "Onboarding" });
		} else {
			next({ name: "Dashboard" });
		}
	} else if (requiredPermission && !authStore.hasPermission(requiredPermission)) {
		// Redirect if user doesn't have required permission
		next({ name: "Dashboard" });
	} else if (requiredRoles && authStore.user && !requiredRoles.includes(authStore.user.role)) {
		// Redirect if user doesn't have required role
		next({ name: "Dashboard" });
	} else if (requiresAuth && authStore.needsOnboarding && to.name !== "Onboarding") {
		// Redirect to onboarding if not completed
		next({ name: "Onboarding" });
	} else if (
		requiresAuth &&
		authStore.isSubscriptionBlocked &&
		to.name !== "SubscriptionBlocked" &&
		to.name !== "BillingPage" &&
		// Retorno do checkout: a assinatura ainda pode estar não-ativa (webhook
		// pendente); a própria tela faz polling até ativar. Não expulsar aqui.
		to.name !== "BillingReturn" &&
		to.name !== "Logout"
	) {
		// Subscription bloqueada — redirecionar para tela de bloqueio
		next({ name: "SubscriptionBlocked" });
	} else {
		next();
	}
});

export default router;
